import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import type { WorksheetAttempt } from '../../Shared/types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<WorksheetAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadHistory();
  }, []);

  const loadHistory = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiClient.getWorksheetHistory();
      setAttempts(data.history || []);
    } catch (err: unknown) {
      setError(extractApiError(err));
      setAttempts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) {
      return 'text-green-600';
    }
    if (score >= 60) {
      return 'text-yellow-600';
    }
    return 'text-red-600';
  };

  return (
    <Navbar>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard 📚</h1>
          <p className="text-lg text-gray-600">Review your completed worksheets and progress</p>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <Card>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => void loadHistory()} variant="secondary">
              Try Again
            </Button>
          </Card>
        ) : attempts.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">No completed worksheets yet</p>
              <Button onClick={() => navigate('/home')}>Start Learning</Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {attempts.map((attempt) => (
              <Card
                key={attempt.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/dashboard/${attempt.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {attempt.worksheet_type === 'diagnostic' ? (
                          '🎯 Diagnostic Assessment'
                        ) : (
                          <>
                            📚{' '}
                            {typeof attempt.topic === 'object'
                              ? attempt.topic.name
                              : attempt.topic || 'Unknown Topic'}
                            <span className="text-sm text-gray-500 ml-2">
                              Pack {attempt.pack_number} • Worksheet {attempt.sequence_index}
                            </span>
                          </>
                        )}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Completed {formatDate(attempt.completed_at)}
                    </p>
                    <div className="flex items-center gap-6 text-sm">
                      <span className={`font-semibold ${getScoreColor(attempt.score_percentage)}`}>
                        {attempt.score_percentage?.toFixed(1)}% Score
                      </span>
                      <span className="text-gray-700">
                        {attempt.correct_count} / {attempt.total_questions} Correct
                      </span>
                      <span className="text-gray-500">
                        {Math.round((attempt.time_spent || 0) / 60)} min
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Navbar>
  );
};

export default Dashboard;
